// Contexto del Carrito - Estado global del carrito de compras
// Proporciona funciones para agregar, eliminar, modificar productos del carrito
// Persiste el carrito en localStorage

import { createContext, useEffect, useMemo, useState } from 'react'
import { useShipping } from '../hooks/useShipping'
import { useBusinessRulesContext } from '../hooks/useBusinessRulesContext'
import { parseNumberLoose } from '../utils/helpers'

/**
 * Contexto del Carrito
 * Maneja el estado global del carrito de compras
 */
const CartContext = createContext(null)

/**
 * Hook para usar el contexto del carrito
 * @returns {Object} { cartItems, addToCart, removeFromCart, updateQuantity, clearCart, getCartTotal, getCartItemCount }
 */
/**
 * Provider del Carrito
 * Envuelve la aplicación y proporciona el estado del carrito
 */
export const CartProvider = ({ children }) => {
  const { settings: shippingSettings } = useShipping()
  const { rules } = useBusinessRulesContext()
  const [cartItems, setCartItems] = useState([])

  const outOfStockPolicy = useMemo(() => {
    return rules?.outOfStockPolicy || { visibility: 'show', purchaseBehavior: 'block_purchase', inquiryChannel: 'whatsapp', labelText: 'Sin stock' }
  }, [rules])

  // Cargar carrito desde localStorage al montar
  useEffect(() => {
    const savedCart = localStorage.getItem('pinohome_cart')
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart)
        setCartItems(parsedCart)
      } catch (error) {
        console.error('Error cargando carrito desde localStorage:', error)
        setCartItems([])
      }
    }
  }, [])

  // Guardar carrito en localStorage cada vez que cambie
  useEffect(() => {
    if (cartItems.length > 0) {
      localStorage.setItem('pinohome_cart', JSON.stringify(cartItems))
    } else {
      localStorage.removeItem('pinohome_cart')
    }
  }, [cartItems])

  /**
   * Agregar un producto al carrito
   * @param {Object} product - Producto a agregar
   * @param {Object} variant - Variante seleccionada (opcional)
   * @param {number} quantity - Cantidad a agregar
   */
  const addToCart = (product, variant = null, quantity = 1) => {
    if (!product || !product.id) {
      console.error('Producto inválido para agregar al carrito')
      return
    }

    setCartItems(prevItems => {
      const buildSelectionKey = (selection) => {
        const entries = Object.entries(selection || {})
          .filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== '')
          .sort(([a], [b]) => a.localeCompare(b))
        if (entries.length === 0) return ''
        return entries.map(([k, v]) => `${k}:${String(v).trim()}`).join('|')
      }

      const variantKey = (() => {
        if (!variant) return ''
        if (variant.selection && typeof variant.selection === 'object') {
          return `sel:${buildSelectionKey(variant.selection)}`
        }
        const type = String(variant.type || '').trim()
        const name = String(variant.name || '').trim()
        if (type || name) return `${type}:${name}`
        return ''
      })()

      const itemId = (() => {
        if (!variantKey) return product.id
        if (variantKey.startsWith('sel:')) {
          return `${product.id}__${variantKey}`
        }
        const type = String(variant?.type || '').trim()
        const name = String(variant?.name || '').trim()
        return `${product.id}_${type}_${name}`
      })()

      // Buscar si el item ya existe en el carrito
      const existingItemIndex = prevItems.findIndex(item => item.id === itemId)

      if (existingItemIndex >= 0) {
        // Si existe, actualizar la cantidad
        const updatedItems = [...prevItems]
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity: updatedItems[existingItemIndex].quantity + quantity
        }
        return updatedItems
      } else {
        // Si no existe, agregar nuevo item
        const basePrice = (product.specialPrice > 0 && product.specialPrice < product.price)
          ? product.specialPrice
          : product.price || 0
        const variantDelta = variant && (variant.priceDelta !== null && variant.priceDelta !== undefined)
          ? variant.priceDelta
          : (variant && variant.price !== null && variant.price !== undefined ? variant.price : null)
        const price = variantDelta !== null ? basePrice + variantDelta : basePrice

        const resolveImageData = (entry) => {
          if (!entry) return null
          if (typeof entry === 'string') return entry
          return entry.fileId || entry.url || null
        }

        const variantImageData = Array.isArray(variant?.images) && variant.images.length > 0 ? variant.images[0] : null
        const productImageData = product.images && product.images.length > 0 ? product.images[0] : null
        const imageData = variantImageData || productImageData
        let image = null
        
        if (imageData) {
          image = resolveImageData(imageData)
          
          if (typeof imageData === 'object' && imageData.fileId && !imageData.fileId.includes('/') && imageData.url) {
            try {
              const urlMatch = imageData.url.match(/\/upload\/[^/]+\/(.+?)(?:\.[^.]+)?$/)
              if (urlMatch && urlMatch[1]) {
                image = urlMatch[1]
              }
            } catch (e) {
              image = imageData.fileId
            }
          }
          
          if (!image) {
            console.warn('Producto sin imagen válida:', { productId: product.id, productName: product.name, imageData })
          }
        } else {
          console.warn('Producto sin imágenes:', { productId: product.id, productName: product.name })
        }

        const normalizedVariant = (() => {
          if (!variant) return null
          if (variant.selection && typeof variant.selection === 'object') {
            const selectionParts = Object.entries(variant.selection)
              .filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== '')
              .map(([k, v]) => `${k}: ${String(v).trim()}`)
            const name = variant.name || selectionParts.join(' | ')
            const sku = variant.sku || ''
            return {
              ...variant,
              type: variant.type || 'variantes',
              name,
              sku,
              selection: variant.selection
            }
          }
          return variant
        })()

        const newItem = {
          id: itemId,
          productId: product.id,
          productName: product.name,
          sku: normalizedVariant ? normalizedVariant.sku : '',
          variant: normalizedVariant,
          price: price,
          quantity: quantity,
          image: image,
          stock: normalizedVariant && normalizedVariant.stock !== null && normalizedVariant.stock !== undefined
            ? normalizedVariant.stock
            : product.stock || 0
        }

        return [...prevItems, newItem]
      }
    })
  }

  /**
   * Eliminar un producto del carrito
   * @param {string} itemId - ID del item a eliminar
   */
  const removeFromCart = (itemId) => {
    setCartItems(prevItems => prevItems.filter(item => item.id !== itemId))
  }

  /**
   * Actualizar la cantidad de un producto en el carrito
   * @param {string} itemId - ID del item
   * @param {number} quantity - Nueva cantidad
   */
  const updateQuantity = (itemId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(itemId)
      return
    }

    setCartItems(prevItems =>
      prevItems.map(item =>
        item.id === itemId
          ? {
            ...item,
            quantity: outOfStockPolicy.purchaseBehavior === 'allow_purchase'
              ? quantity
              : Math.min(quantity, item.stock || 999)
          }
          : item
      )
    )
  }

  /**
   * Vaciar el carrito completamente
   */
  const clearCart = () => {
    setCartItems([])
    localStorage.removeItem('pinohome_cart')
  }

  /**
   * Calcular el total del carrito
   * @returns {number} Total del carrito
   */
  const getCartTotal = () => {
    return cartItems.reduce((total, item) => {
      const price = parseNumberLoose(item?.price)
      const quantity = parseNumberLoose(item?.quantity)
      if (!Number.isFinite(price) || !Number.isFinite(quantity) || quantity <= 0) return total
      return total + (price * quantity)
    }, 0)
  }

  /**
   * Contar el número total de items en el carrito
   * @returns {number} Número total de items
   */
  const getCartItemCount = () => {
    return cartItems.reduce((count, item) => {
      const quantity = parseNumberLoose(item?.quantity)
      if (!Number.isFinite(quantity) || quantity <= 0) return count
      return count + quantity
    }, 0)
  }

  /**
   * Obtener el subtotal (sin envío)
   * @returns {number} Subtotal
   */
  const getSubtotal = () => {
    return getCartTotal()
  }

  const getShippingQuote = (distance = null) => {
    if (!shippingSettings) {
      const subtotal = getSubtotal()
      if (subtotal >= 50000) {
        return { cost: 0, type: 'free' }
      }
      return { cost: 15000, type: 'standard' }
    }

    if (!shippingSettings.enabled) {
      return { cost: 0, type: 'disabled' }
    }

    const subtotal = getSubtotal()
    const minAmount = shippingSettings.freeShippingMinAmount || 50000
    const maxDistance = shippingSettings.freeShippingMaxDistance
    const defaultCost = shippingSettings.defaultShippingCost || 15000
    const distancePricing = shippingSettings.distancePricing || {}
    const tiers = Array.isArray(distancePricing.tiers)
      ? distancePricing.tiers
        .map((t) => ({
          maxDistance: Number(t?.maxDistance),
          cost: Number(t?.cost)
        }))
        .filter((t) => Number.isFinite(t.maxDistance) && t.maxDistance > 0 && Number.isFinite(t.cost) && t.cost >= 0)
        .sort((a, b) => a.maxDistance - b.maxDistance)
      : []
    const negotiationDistance = parseNumberLoose(distancePricing.negotiationDistance)
    const negotiationEnabled = distancePricing.negotiationEnabled === true || distancePricing.negotiationEnabled === 'true'
    const maxDistanceValue = Number(maxDistance)
    const hasFreeMaxDistance = maxDistance !== null && Number.isFinite(maxDistanceValue) && maxDistanceValue > 0
    const hasNegotiationDistance = negotiationEnabled && Number.isFinite(negotiationDistance) && negotiationDistance > 0
    const requiresDistance = tiers.length > 0 || hasFreeMaxDistance || hasNegotiationDistance

    if (distance === null && requiresDistance) {
      return { cost: defaultCost, type: 'pending' }
    }

    if (hasNegotiationDistance && distance !== null && distance > negotiationDistance) {
      return { cost: 0, type: 'negotiation' }
    }

    const qualifiesByAmount = subtotal >= minAmount
    let qualifiesByDistance = true
    if (hasFreeMaxDistance) {
      qualifiesByDistance = distance <= maxDistanceValue
    }

    if (qualifiesByAmount && qualifiesByDistance) {
      return { cost: 0, type: 'free' }
    }

    if (distance !== null && tiers.length > 0) {
      const tier = tiers.find((t) => distance <= t.maxDistance)
      if (tier) {
        return { cost: tier.cost, type: 'tier', tier }
      }
    }

    return { cost: defaultCost, type: 'standard' }
  }

  const getShippingCost = (distance = null) => {
    return getShippingQuote(distance).cost
  }

  /**
   * Obtener el total final (subtotal + envío)
   * @param {number} distance - Distancia en kilómetros (opcional)
   * @returns {number} Total final
   */
  const getFinalTotal = (distance = null) => {
    const quote = getShippingQuote(distance)
    const shipping = quote.type === 'pending' || quote.type === 'negotiation' ? 0 : quote.cost
    return getSubtotal() + shipping
  }

  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartItemCount,
    getSubtotal,
    getShippingQuote,
    getShippingCost,
    getFinalTotal
  }

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  )
}

export default CartContext

